import { useEffect, useState } from "react";
import { Lock, Upload, Award, AlertOctagon, Paperclip, Send, FileDown } from "lucide-react";
import { api, apiError, API, descargarBoletinFamilia, descargarPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fecha } from "@/lib/format";
import { Loading, PageTitle, Stat, Empty } from "@/components/Common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const estadoBadge = { pendiente: "destructive", entregada: "secondary", evaluada: "default" };

export default function Academico() {
  const { activeId } = useAuth();
  const [tareas, setTareas] = useState(null);
  const [boletin, setBoletin] = useState(null);
  const [asis, setAsis] = useState(null);
  const [honor, setHonor] = useState(null);
  const [filtro, setFiltro] = useState("todas");
  const [materia, setMateria] = useState("todas");
  const [entrega, setEntrega] = useState(null);

  const cargar = () => {
    api.get(`/estudiantes/${activeId}/tareas`).then((r) => setTareas(r.data));
    api.get(`/estudiantes/${activeId}/calificaciones`).then((r) => setBoletin(r.data));
    api.get(`/estudiantes/${activeId}/asistencia`).then((r) => setAsis(r.data));
    api.get(`/portal/cuadro-honor/${activeId}`).then((r) => setHonor(r.data)).catch(() => setHonor(null));
  };

  useEffect(() => {
    if (!activeId) return;
    setTareas(null); setBoletin(null); setAsis(null); setHonor(null);
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const adjuntar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("El archivo no debe superar los 5 MB");
    const reader = new FileReader();
    reader.onload = () => setEntrega({
      ...entrega, archivo_nombre: file.name, archivo_base64: reader.result,
    });
    reader.readAsDataURL(file);
  };

  const entregar = async () => {
    try {
      await api.post(`/estudiantes/${activeId}/tareas/${entrega.tarea.id}/entregar`, {
        comentario: entrega.comentario || "Entregado desde el portal",
        archivo_nombre: entrega.archivo_nombre || null,
        archivo_base64: entrega.archivo_base64 || null,
      });
      toast.success("Tarea entregada. El docente podrá revisarla y calificarla.");
      setEntrega(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!activeId) return <Empty>No hay estudiantes asociados.</Empty>;
  if (!tareas || !boletin || !asis) return <Loading />;

  const materias = [...new Set(tareas.map((t) => t.materia_nombre))];
  const visibles = tareas.filter(
    (t) => (filtro === "todas" || t.estado === filtro) && (materia === "todas" || t.materia_nombre === materia)
  );

  const periodos = [...new Set(boletin.calificaciones.map((c) => c.periodo))];

  return (
    <div data-testid="academic-page">
      <PageTitle title="Módulo académico"
                 subtitle="Tareas, boletín de calificaciones, asistencia y conducta del estudiante seleccionado.">
        <Button variant="outline" className="rounded-full" data-testid="descargar-boletin-familia"
                onClick={async () => {
                  try {
                    await descargarBoletinFamilia();
                    toast.success("Boletín de la familia descargado");
                  } catch (e) {
                    toast.error(apiError(e, "No se pudo generar el boletín"));
                  }
                }}>
          <FileDown className="mr-2 h-4 w-4" /> Boletín de todos mis hijos
        </Button>
      </PageTitle>

      {honor?.en_cuadro_honor && (
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-accent/40 bg-accent/5 p-6"
             data-testid="cuadro-honor-banner">
          <div className="flex items-start gap-3">
            <Award className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
            <div>
              <h3 className="font-display text-base font-bold sm:text-lg">
                ¡{honor.puesto_texto} en el cuadro de honor!
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {honor.grado} · Promedio general <strong className="font-mono">{honor.promedio}</strong>
                {honor.anio_lectivo ? ` · Año lectivo ${honor.anio_lectivo}` : ""}
              </p>
            </div>
          </div>
          <Button variant="outline" className="rounded-full" data-testid="descargar-diploma"
                  onClick={async () => {
                    try {
                      await descargarPdf(`/portal/cuadro-honor/${activeId}/diploma`, {}, "Diploma.pdf");
                      toast.success("Diploma descargado");
                    } catch (e) {
                      toast.error(apiError(e, "No se pudo generar el diploma"));
                    }
                  }}>
            <FileDown className="mr-2 h-4 w-4" /> Descargar el diploma
          </Button>
        </div>
      )}

      <Tabs defaultValue="tareas">
        <TabsList data-testid="academic-tabs">
          <TabsTrigger value="tareas" data-testid="tab-tareas">Tareas</TabsTrigger>
          <TabsTrigger value="boletin" data-testid="tab-boletin">Boletín</TabsTrigger>
          <TabsTrigger value="asistencia" data-testid="tab-asistencia">Asistencia y conducta</TabsTrigger>
        </TabsList>

        <TabsContent value="tareas" className="mt-8">
          <div className="mb-6 flex flex-wrap gap-3">
            <select data-testid="filtro-estado" value={filtro} onChange={(e) => setFiltro(e.target.value)}
                    className="rounded-full border border-border bg-card px-4 py-2 text-sm">
              <option value="todas">Todos los estados</option>
              <option value="pendiente">Pendientes</option>
              <option value="entregada">Entregadas</option>
              <option value="evaluada">Evaluadas</option>
            </select>
            <select data-testid="filtro-materia" value={materia} onChange={(e) => setMateria(e.target.value)}
                    className="rounded-full border border-border bg-card px-4 py-2 text-sm">
              <option value="todas">Todas las materias</option>
              {materias.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <div className="grid gap-6 lg:grid-cols-2" data-testid="lista-tareas">
            {visibles.map((t) => (
              <article key={t.id} className="lift rounded-lg border border-border/60 bg-card p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t.materia_nombre}</p>
                    <h3 className="mt-2 font-display text-lg font-semibold">{t.titulo}</h3>
                  </div>
                  <span className="flex flex-col items-end gap-2">
                    <Badge variant={estadoBadge[t.estado]} data-testid={`tarea-estado-${t.id}`}>{t.estado}</Badge>
                    {t.origen === "classroom" && (
                      <Badge variant="outline" data-testid={`tarea-classroom-${t.id}`}>Classroom</Badge>
                    )}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{t.descripcion}</p>
                <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                  <span className="font-mono">Entrega: {fecha(t.fecha_entrega)}</span>
                  <span>{t.docente}</span>
                  {t.archivo_nombre && (
                    <a href={`${API}/tareas/${t.id}/archivo`} target="_blank" rel="noreferrer"
                       data-testid={`archivo-tarea-${t.id}`}
                       className="flex items-center gap-1 text-primary hover:underline">
                      <Paperclip className="h-3 w-3" /> {t.archivo_nombre}
                    </a>
                  )}
                  {t.entrega_archivo_nombre && t.entrega_id && (
                    <a href={`${API}/entregas/${t.entrega_id}/archivo`} target="_blank" rel="noreferrer"
                       data-testid={`mi-entrega-${t.id}`}
                       className="flex items-center gap-1 text-secondary-foreground hover:underline">
                      <Upload className="h-3 w-3" /> Mi entrega: {t.entrega_archivo_nombre}
                    </a>
                  )}
                  {t.adjunto_nombre && (
                    <span className="flex items-center gap-1 text-primary">
                      <Upload className="h-3 w-3" /> {t.adjunto_nombre}
                    </span>
                  )}
                  {(t.materiales || []).map((m, i) => (
                    <a key={i} href={m.url} target="_blank" rel="noreferrer"
                       data-testid={`material-tarea-${t.id}-${i}`}
                       className="flex items-center gap-1 text-primary hover:underline">
                      <Paperclip className="h-3 w-3" /> {m.titulo || m.tipo}
                    </a>
                  ))}
                </div>
                {t.enlace && (
                  <a href={t.enlace} target="_blank" rel="noreferrer"
                     data-testid={`abrir-classroom-${t.id}`}
                     className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary/40 px-4 py-2 text-xs font-semibold text-primary lift">
                    Abrir en Google Classroom
                  </a>
                )}
                {t.estado === "pendiente" && (
                  <Button className="mt-6 rounded-full" size="sm"
                          onClick={() => setEntrega({ tarea: t, comentario: "", archivo_nombre: "", archivo_base64: "" })}
                          data-testid={`entregar-tarea-${t.id}`}>
                    Entregar tarea
                  </Button>
                )}
                {t.nota != null && <p className="mt-4 font-mono text-sm">Nota: {t.nota}</p>}
              </article>
            ))}
            {visibles.length === 0 && <Empty testId="tareas-vacias">No hay tareas con estos filtros.</Empty>}
          </div>
        </TabsContent>

        <TabsContent value="boletin" className="mt-8">
          {boletin.bloqueado ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-10 text-center"
                 data-testid="boletin-bloqueado">
              <Lock className="mx-auto h-8 w-8 text-destructive" />
              <h3 className="mt-4 font-display text-xl font-bold">Boletín bloqueado</h3>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">{boletin.mensaje}</p>
            </div>
          ) : (
            <div data-testid="boletin">
              <div className="grid gap-6 sm:grid-cols-3">
                <Stat label="Promedio acumulado" value={boletin.promedio} tone="good" testId="boletin-promedio" />
                <Stat label="Materias evaluadas"
                      value={new Set(boletin.calificaciones.map((c) => c.materia_id)).size} />
                <Stat label="Periodos publicados" value={periodos.length} />
              </div>
              {periodos.map((p) => (
                <div key={p} className="mt-8 overflow-hidden rounded-lg border border-border/60 bg-card">
                  <div className="border-b border-border px-6 py-4">
                    <h3 className="font-display text-lg font-semibold">{p}</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                      <tr>
                        <th className="px-6 py-3">Materia</th>
                        <th className="px-6 py-3">Nota</th>
                        <th className="hidden px-6 py-3 sm:table-cell">Observaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boletin.calificaciones.filter((c) => c.periodo === p).map((c) => (
                        <tr key={c.id} className="border-t border-border/60 hover:bg-muted/30">
                          <td className="px-6 py-4 font-medium">{c.materia_nombre}</td>
                          <td className={`px-6 py-4 font-mono font-semibold ${c.nota < 70 ? "text-destructive" : "text-primary"}`}>
                            {c.nota}
                          </td>
                          <td className="hidden px-6 py-4 text-muted-foreground sm:table-cell">{c.observaciones}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="asistencia" className="mt-8">
          <div className="grid gap-6 sm:grid-cols-4" data-testid="asistencia-resumen">
            <Stat label="Asistencia" value={`${asis.resumen.porcentaje}%`} tone="good" />
            <Stat label="Presencias" value={asis.resumen.presente} />
            <Stat label="Tardanzas" value={asis.resumen.tardanza} tone="warn" />
            <Stat label="Ausencias" value={asis.resumen.ausente} tone="bad" />
          </div>

          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <div className="rounded-lg border border-border/60 bg-card p-6 sm:p-8">
              <h3 className="font-display text-lg font-semibold">Registro reciente</h3>
              <div className="mt-5 space-y-3" data-testid="asistencia-registros">
                {asis.registros.slice(0, 12).map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-muted-foreground">{fecha(r.fecha)}</span>
                    <Badge variant={r.estado === "presente" ? "secondary" : r.estado === "tardanza" ? "outline" : "destructive"}>
                      {r.estado}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-border/60 bg-card p-6 sm:p-8">
              <h3 className="font-display text-lg font-semibold">Conducta y reconocimientos</h3>
              <div className="mt-5 space-y-5" data-testid="conducta-registros">
                {asis.conducta.map((c) => (
                  <div key={c.id} className="flex gap-3">
                    {c.tipo === "reconocimiento"
                      ? <Award className="mt-1 h-4 w-4 shrink-0 text-primary" />
                      : <AlertOctagon className="mt-1 h-4 w-4 shrink-0 text-destructive" />}
                    <div>
                      <p className="text-sm leading-relaxed">{c.descripcion}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{fecha(c.fecha)}</p>
                    </div>
                  </div>
                ))}
                {asis.conducta.length === 0 && <p className="text-sm text-muted-foreground">Sin registros.</p>}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!entrega} onOpenChange={(o) => !o && setEntrega(null)}>
        <DialogContent data-testid="modal-entregar-tarea">
          <DialogHeader>
            <DialogTitle className="font-display">Entregar tarea</DialogTitle>
            <DialogDescription>{entrega?.tarea?.titulo} · {entrega?.tarea?.materia_nombre}</DialogDescription>
          </DialogHeader>
          {entrega && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Comentario para el docente (opcional)</Label>
                <Textarea rows={3} value={entrega.comentario} data-testid="entrega-comentario"
                          onChange={(e) => setEntrega({ ...entrega, comentario: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Archivo de la tarea (foto, PDF o documento)</Label>
                <Input type="file" onChange={adjuntar} data-testid="entrega-archivo"
                       accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*" />
                {entrega.archivo_nombre && (
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Paperclip className="h-3 w-3" /> {entrega.archivo_nombre}
                  </p>
                )}
              </div>
              <Button className="rounded-full py-6" onClick={entregar} data-testid="confirmar-entrega">
                <Send className="mr-2 h-4 w-4" /> Enviar entrega
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
